import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, FormikProps, Form, FormikHelpers } from 'formik';
import { inject, observer } from 'mobx-react';
import get from 'lodash.get';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import TextField from '@material-ui/core/TextField';

import Switch from 'components/FormFields/Switch';
import FormNumberField from 'components/FormFields/FormNumberField';
import FormSelectField from 'components/FormFields/FormSelectField';
import withoutEnterSubmit from 'components/withoutEnterSubmit';
import compose from 'helpers/compose';
import { scrollToTop } from 'helpers/helpers';
import Background from 'components/Layout/Background';
import Info from 'components/Tooltip/Info';

import { WorkflowInsuranceStore, Insurance as IInsurance } from 'stores/workflowStores/finances/workflowInsuranceStore';
import { AccountEditBasic } from 'stores/workflowStores/workflowBasicStore/model';
import { Partner } from 'stores/workflowStores/finances/planningFor/model';
import { RentModalsStore } from 'stores/workflowStores/finances/rentModalsStore';
import { RootStore } from 'stores/rootStore';
import { useTwoColumns } from 'hooks/useTwoColumns';
import { Currency } from 'enums/index';

const FormWithoutEnterSubmit = withoutEnterSubmit(Form);

interface Props {
  accountId: string;
  currency: Currency;
  partner: Partner;
  classes: Record<string, string>;
  basicData: AccountEditBasic;
  rentModalsStore: RentModalsStore;
  rootStore: RootStore;
  detailMode?: boolean;
}

const Insurance: React.FC<Props> = ({
  accountId,
  currency,
  partner,
  classes,
  basicData,
  rentModalsStore,
  rootStore,
  detailMode,
}) => {
  const [store] = useState(new WorkflowInsuranceStore(rootStore, rentModalsStore, accountId, detailMode));
  const { t } = useTranslation();
  const { twoColumns } = useTwoColumns();

  useEffect(() => {
    scrollToTop('.MuiDialogContent-root');
    store.fetchInsurance();
  }, [store]);

  const { data } = store;

  const { onNextRentTab, rentFormKey, setRentSubmitting } = rentModalsStore;

  const translatedCurrency = t(`currency_${currency}`);

  const renderInsuranceForm = (label: string, formikProps: FormikProps<IInsurance>, forPartner = false) => {
    const target = forPartner ? 'partnerData' : 'clientData';
    const { defaultData, [target]: targetData } = formikProps.values;
    const hasInsurance = targetData && Object.keys(targetData).length > 1;
    let returnOnlyHeader = false; // return only header with info that someone is in pension
    let displayName = '';

    const dataToUse = hasInsurance ? targetData : defaultData;

    const handleHasInsuranceChange = () => {
      formikProps.setFieldValue(target, hasInsurance ? undefined : dataToUse);
    };

    const silentUpdate = async () => {
      setRentSubmitting(true);
      await store.silentUpdate(formikProps);
      setRentSubmitting(false);
    };

    if (!forPartner && basicData?.client?.retired) {
      returnOnlyHeader = true;
      displayName = t('finQuestionnary_pension_clientAlreadyInPension');
    } else if (forPartner && partner.retired) {
      returnOnlyHeader = true;
      displayName = t('finQuestionnary_pension_partnerAlreadyInPension');
    } else {
      displayName = forPartner ? partner.firstname : get(basicData, 'client.firstname');
    }

    const header = <Typography variant="h3">{displayName}</Typography>;

    if (returnOnlyHeader) {
      return (
        <Background inside className={classes.paperContent}>
          {header}
        </Background>
      );
    }

    return (
      <Grid container>
        <Grid item xs={12}>
          {header}
        </Grid>
        <Grid item xs={12}>
          <Switch
            afterChange={handleHasInsuranceChange}
            checked={!!hasInsurance}
            name={`${target}.hasInsurance`}
            label={t('finQuestionnary_insurance')}
          />
        </Grid>

        {hasInsurance && (
          <>
            <Grid item xs={12}>
              <FormNumberField
                name={`${target}.balance.value`}
                label={t('finQuestionnary_pension_actualBalance')}
                endAdornment={translatedCurrency}
                afterBlur={silentUpdate}
              />
            </Grid>

            <Grid item xs={12}>
              <FormNumberField
                name={`${target}.periodicPerson.value`}
                label={t('finQuestionnary_pension_monthContributionClient')}
                endAdornment={translatedCurrency}
                afterBlur={silentUpdate}
              />
            </Grid>

            <Grid item xs={12}>
              <FormNumberField
                name={`${target}.periodicEmployer.value`}
                label={t('finQuestionnary_pension_monthContributionEmployer')}
                endAdornment={translatedCurrency}
                afterBlur={silentUpdate}
              />
            </Grid>

            <Grid item xs={12}>
              <FormSelectField
                name={`${target}.strategy`}
                label={t('finQuestionnary_pension_actualInvestmentStrategy')}
                afterChange={silentUpdate}
                options={formikProps.values.options.strategy
                  // slice to make a copy and not mutate array by reversing it in every render
                  .slice()
                  .map((strategy) => ({
                    value: strategy,
                    label: t(`finQuestionnary_insurance_strategy_${strategy}`),
                  }))}
                tooltipContent={t('tooltip_insurance_actualInvestmentStrategy')}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1">
                {t('finQuestionnary_pension_DPSrentCount')}
                <Info content={t('tooltip_insurance_DPSrentCount')} heading />
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <FormSelectField
                name={`${target}.calculationMode`}
                label={t('finQuestionnary_pension_guess')}
                afterChange={silentUpdate}
                options={formikProps.values.options.calculationMode.map((mode) => ({
                  value: mode,
                  label: t(`calculationMode_${mode}`),
                }))}
                tooltipContent={t('tooltip_insurance_calculationMode')}
              />
            </Grid>

            <Grid item xs={6}>
              {formikProps.values[target].calculationMode === 'MANUAL' && (
                <>
                  <FormNumberField
                    name={`${target}.plannedRent.value`}
                    label={t('general_rent_monthly')}
                    endAdornment={translatedCurrency}
                    InputLabelProps={{ shrink: true }}
                    afterBlur={silentUpdate}
                  />
                  <TextField
                    name={`${target}.plannedRent.currency`}
                    type="hidden"
                    style={{ visibility: 'hidden' }}
                    value={currency}
                  />
                </>
              )}

              {formikProps.values[target].calculationMode === 'CALCULATED' && (
                <>
                  <FormNumberField
                    name={`${target}.calculatedRent.value`}
                    label={t('general_rent_monthly')}
                    endAdornment={translatedCurrency}
                    InputLabelProps={{ shrink: true }}
                    afterBlur={formikProps.submitForm}
                    readOnly
                  />
                  <TextField
                    name={`${target}.calculatedRent.currency`}
                    type="hidden"
                    style={{ visibility: 'hidden' }}
                    value={currency}
                  />
                </>
              )}
            </Grid>
          </>
        )}
      </Grid>
    );
  };

  const handleSubmit = async (values: IInsurance, formikHelpers: FormikHelpers<IInsurance>) => {
    rentModalsStore.setRentSubmitting(true);

    // if there is only client and he is retired or if both client and partner are retired
    // do not even submit and go to nextRentTab right away
    if ((partner?.retired && basicData.client.retired) || (basicData.client.retired && !partner)) {
      rentModalsStore.setRentSubmitting(false);
      onNextRentTab();
      return;
    }

    const response = await store.submitInsurance(values, formikHelpers);
    rentModalsStore.setRentSubmitting(false);

    response && onNextRentTab();
  };

  return (
    <Formik initialValues={data || ({} as IInsurance)} onSubmit={handleSubmit} enableReinitialize>
      {(formikProps) => (
        <FormWithoutEnterSubmit id={rentFormKey}>
          <Background inside className={twoColumns}>
            {data && (
              <>
                {basicData && renderInsuranceForm(t('finQuestionnary_insurance_title_client'), formikProps)}
                {partner && renderInsuranceForm(t('finQuestionnary_insurance_title_partner'), formikProps, true)}
              </>
            )}
          </Background>
        </FormWithoutEnterSubmit>
      )}
    </Formik>
  );
};

export default compose(inject('rootStore'), observer)(Insurance);
